import { useQuery, UseQueryOptions } from '@tanstack/react-query';

import collectiveApi from 'modules/common/collectiveApi';

interface MemberDocument {
    id: string;
    uuid: string;
    file_name: string;
    file_format: string;
    document_type: string;
    created_at: string;
    month?: number;
    quarter?: number;
    year?: number;
}

interface MemberDocumentsByUuidsResponse {
    documents: MemberDocument[];
    total_count: number;
}

/**
 * Hook to fetch multiple member documents by their UUIDs
 * This is more efficient than querying all documents and filtering
 */
export default function useMemberDocumentsByUuids(
    documentUuids: string[],
    options?: Omit<UseQueryOptions<MemberDocumentsByUuidsResponse>, 'queryKey' | 'queryFn'>
) {
    return useQuery({
        queryKey: ['memberDocuments', 'byUuids', documentUuids],
        queryFn: async (): Promise<MemberDocumentsByUuidsResponse> => {
            if (documentUuids.length === 0) {
                return { documents: [], total_count: 0 };
            }

            // Fetch documents in parallel for better performance
            const promises = documentUuids.map(async (uuid) => {
                try {
                    const response = await collectiveApi.get<MemberDocument>(`/documents/member-documents/${uuid}/`);
                    return response.data;
                } catch (error) {
                    console.warn(`Failed to fetch document ${uuid}:`, error);
                    return null; // Return null for failed requests
                }
            });

            const results = await Promise.all(promises);

            // Filter out null results (failed requests)
            const documents = results.filter((doc): doc is MemberDocument => doc !== null);

            return {
                documents,
                total_count: documents.length,
            };
        },
        enabled: documentUuids.length > 0,
        staleTime: 1000 * 60 * 5, // 5 minutes
        retry: 2, // Retry failed requests twice
        ...options,
    });
}
